import type { HoneybookStage } from '../dto/honeybook.dto.js'
import type { FunnelStage } from '../enums/funnel-stage.enum.js'

const HONEYBOOK_APP_ORIGIN = 'https://app.honeybook.com'

/**
 * A project's page in HoneyBook's own app, where the venue works on it. HoneyBook
 * calls a project an "event" in its addresses; the workspace is the one around
 * it. The shape was read off a real account on 2026-10-22.
 */
export function honeybookProjectUrl(projectId: string, workspaceId: string): string {
  return `${HONEYBOOK_APP_ORIGIN}/app/event/${encodeURIComponent(projectId)}/workspace/${encodeURIComponent(workspaceId)}/activity`
}

/**
 * HoneyBook's built-in stages this app knows by their fixed key. HoneyBook's
 * reference names `_INQUIRY_`; the first real account (2026-10-22) keyed its
 * first stage `_NEW_` and its last `_CLOSED_` ("Completed", among bookings, so
 * Booked by its category), and left every other default stage, Proposal Sent
 * and Retainer Paid among them, without a key. A stage not here is the venue's
 * to say what it means, built-in or its own.
 */
export const HONEYBOOK_BUILT_IN_STAGES: Readonly<Record<string, FunnelStage>> = {
  _INQUIRY_: 'inquiry',
  _NEW_: 'inquiry',
}

/**
 * What a stage counts as whatever the venue says: a known built-in stage by its
 * key, a stage HoneyBook puts among bookings as Booked. Null for one the venue
 * says the meaning of.
 */
export function honeybookFixedTarget(stage: Pick<HoneybookStage, 'systemKey' | 'category'>): FunnelStage | null {
  const builtIn = ownValue(HONEYBOOK_BUILT_IN_STAGES, stage.systemKey)
  if (builtIn !== undefined) return builtIn
  return stage.category === 'booked' ? 'booked' : null
}

/**
 * A record's own value for a key, never one it inherits: a key read from
 * HoneyBook (a stage id, a meeting title's key) can be `constructor`.
 */
export function ownValue<T>(record: Readonly<Record<string, T>>, key: string | null): T | undefined {
  return key !== null && Object.hasOwn(record, key) ? record[key] : undefined
}
